import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from '../clsx';
import { useEscapeKey, useFocusTrap, useStableId } from '../hooks';
import { Eyebrow } from './Eyebrow';
import { Overlay } from './Overlay';
import styles from './Sheet.module.css';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode | undefined;
  eyebrow?: ReactNode | undefined;
  subtitle?: ReactNode | undefined;
  /** Accessible name when there is no title. */
  label?: string | undefined;
  footer?: ReactNode | undefined;
  footerNote?: ReactNode | undefined;
  /** `auto` fits the content (max. 90 %); `full` takes 90 % of the viewport. */
  height?: 'auto' | 'full' | undefined;
  closeOnScrim?: boolean | undefined;
  closeOnEscape?: boolean | undefined;
  initialFocus?: RefObject<HTMLElement | null> | 'first' | 'container' | 'none' | undefined;
  id?: string | undefined;
  className?: string | undefined;
  /** Positioned frame to render into instead of `document.body`. */
  container?: HTMLElement | null | undefined;
  children: ReactNode;
}

let lockCount = 0;
export function lockBodyScroll(): () => void {
  if (lockCount === 0) {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
  }
  lockCount += 1;
  return () => {
    lockCount -= 1;
    if (lockCount === 0) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };
}

/** Bottom sheet of the mobile layouts (Responsive Regeln 05): handle, header, scrolling body, footer; max. 90 % height. */
export function Sheet({
  open,
  onClose,
  title,
  eyebrow,
  subtitle,
  label,
  footer,
  footerNote,
  height = 'auto',
  closeOnScrim = true,
  closeOnEscape = true,
  initialFocus = 'first',
  id,
  className,
  container,
  children,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const sheetId = useStableId(id);
  const titleId = `${sheetId}-title`;
  useEscapeKey(closeOnEscape ? onClose : undefined, open);
  useFocusTrap(panelRef, open, { initialFocus });
  useEffect(() => (open && !container ? lockBodyScroll() : undefined), [open, container]);

  if (!open) return null;
  return createPortal(
    <Overlay
      top={0}
      onScrimClick={closeOnScrim ? onClose : undefined}
      absolute={Boolean(container)}
      className={styles.overlay}
    >
      <div
        ref={panelRef}
        id={sheetId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : label}
        tabIndex={-1}
        className={clsx(styles.sheet, height === 'full' && styles.full, className)}
      >
        <div className={styles.handle} aria-hidden="true" />
        {title || eyebrow ? (
          <div className={styles.header}>
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            {title ? (
              <div id={titleId} className={styles.title}>
                {title}
              </div>
            ) : null}
            {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
          </div>
        ) : null}
        <div className={styles.body}>{children}</div>
        {footer || footerNote ? (
          <div className={styles.footer}>
            {footerNote ? <span className={styles.footerNote}>{footerNote}</span> : null}
            {footer ? <span className={styles.footerActions}>{footer}</span> : null}
          </div>
        ) : null}
      </div>
    </Overlay>,
    container ?? document.body,
  );
}
